import { useEffect, useMemo, useState } from 'react'
import {
  Dialog, DialogContent, DialogTitle, Divider, Box, Typography,
  CircularProgress, TextField, ToggleButton, ToggleButtonGroup, Checkbox,
} from '@mui/material'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import ViewListIcon from '@mui/icons-material/ViewList'
import { BiShapeTriangle } from 'react-icons/bi'
import CfpSelect from '../../comps/CfpSelect'
import { FancyButton } from '../../comps/FancyButtons'
import { DataTable, type DataTableColumn } from '../../comps/DataTable'
import { useApp, useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { FS_LG, FS_SM, FS_XS } from '../../common/theme'
import type { Question } from '../../types'
import { executeGenerateBatch } from '../../services/QuestionActions'

type SourcePick = 'auto' | 'all' | 'none' | 'project'
type WorkflowStep = 'sources' | 'structure' | 'answer' | 'trace' | 'score'

interface WorkflowRow {
  key: WorkflowStep
  label: string
  description: string
}

// Mirrors the rows in BidQuestionPlanTab so the workflow datatable
// reads as a per-question pipeline picker.
const WORKFLOW_ROWS: WorkflowRow[] = [
  { key: 'sources', label: 'Sources', description: 'Pick relevant sources from the knowledge base' },
  { key: 'structure', label: 'Storyboard', description: 'Build the storyboard / structure for the answer' },
  { key: 'answer', label: 'Answer', description: 'Generate the answer text from sources + structure' },
  { key: 'trace', label: 'Trace', description: 'Trace sentences back to the source material' },
  { key: 'score', label: 'Estimated Score', description: 'Esimtate potential score against a template' },
]

// Preset shortcuts - each button up top sets the checkbox selection to
// its named set. The "active" highlight on the ToggleButtonGroup is
// derived from the current selection, so the user can tweak the
// checkboxes afterwards and the buttons drop their highlight (custom
// state) without anything breaking.
type Preset = 'all' | 'generate' | 'trace' | 'score'
const PRESETS: Record<Preset, WorkflowStep[]> = {
  all: ['sources', 'structure', 'answer', 'trace', 'score'],
  generate: ['sources', 'structure', 'answer'],
  trace: ['trace'],
  score: ['score'],
}

function matchPreset(selected: Set<WorkflowStep>): Preset | null {
  for (const [name, steps] of Object.entries(PRESETS) as [Preset, WorkflowStep[]][]) {
    if (selected.size === steps.length && steps.every(s => selected.has(s))) return name
  }
  return null
}

interface Props {
  open: boolean
  projectsha?: string
  // Pre-loaded list of questions for this batch run.
  questions: any[]
  projectbideval?: any
  defaultStyleName?: string
  onClose: () => void
  onQuestionChange?: (q: Question) => void
}

export default function BidBatchProcessModal({
  open, projectsha, questions, projectbideval, defaultStyleName, onClose,
}: Props) {
  const { styleNames, currentStyleName } = useApp()
  const { updateQuestion } = useAppActions()

  const [pickSelected, setPickSelected] = useState<SourcePick>('auto')
  const [pickN, setPickN] = useState(3)
  const [styleName, setStyleName] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  // Workflow row selection - the source of truth. The preset buttons
  // up top just rewrite this set.
  const [workflowSelected, setWorkflowSelected] = useState<Set<WorkflowStep>>(
    () => new Set<WorkflowStep>(PRESETS.all),
  )

  // Reset to sensible defaults whenever the modal opens.
  useEffect(() => {
    if (!open) return
    setPickSelected('auto')
    setPickN(3)
    setStyleName(defaultStyleName || currentStyleName || (styleNames[0] ?? ''))
    setWorkflowSelected(new Set<WorkflowStep>(PRESETS.all))
    setSubmitting(false)
  }, [open, currentStyleName, styleNames])

  const toggleWorkflowStep = (key: WorkflowStep) => {
    setWorkflowSelected(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  const applyPreset = (preset: Preset) => {
    setWorkflowSelected(new Set<WorkflowStep>(PRESETS[preset]))
  }

  const activePreset = useMemo(() => matchPreset(workflowSelected), [workflowSelected])

  const isSingle = questions.length === 1
  const title = isSingle ? 'Question Action' : 'Batch Process'

  const todoCount = questions.length
  const project = { projectsha: projectsha ?? '', projectbideval } as any

  const workflowHasSources =
    workflowSelected.has('sources')
  const workflowNeedsStyle =
    workflowSelected.has('structure') ||
    workflowSelected.has('answer')
  const workflowAny = workflowHasSources || workflowNeedsStyle || workflowSelected.has('trace') || workflowSelected.has('score')

  const handleStart = async () => {
    if (!projectsha || questions.length === 0 || todoCount === 0 || !workflowAny) return
    setSubmitting(true)
    try {
      await executeGenerateBatch(questions, project, styleName, updateQuestion, {
        pickselected: pickSelected,
        n: pickN,
        gensources: workflowSelected.has('sources'),
        genstructure: workflowSelected.has('structure'),
        genanswer: workflowSelected.has('answer'),
        gentrace: workflowSelected.has('trace'),
        genscore: workflowSelected.has('score'),
      })
      onClose()
    } catch {
      addToast('error', 'Action failed')
    } finally {
      setSubmitting(false)
    }
  }

  const startLabel = `Run (${questions.length})`

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ 'data-testid': 'batchprocess_dlg' } as any}>
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography data-testid="batchprocess_title" component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          {title}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 2.5, pt: 2, pb: 2 }}>

        <Typography data-testid="batchprocess_count" variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1.5, fontSize: FS_LG, fontWeight: 500 }}>
          {questions.length} {questions.length === 1 ? 'Question' : 'Questions'} Selected
        </Typography>

        {/* Preset shortcuts - clicking one overwrites the workflow
            selection below. The highlight drops to no-selection when
            the user customises the checkboxes. */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            value={activePreset}
            onChange={(_, v) => v && applyPreset(v as Preset)}
            size="small"
            data-testid="batchprocess_mode"
          >
            <ToggleButton value="all" data-testid="batchprocess_mode_all" sx={{ gap: 0.75, px: 1.5 }}>
              <ViewListIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontSize: FS_SM, textTransform: 'none' }}>
                All
              </Typography>
            </ToggleButton>
            <ToggleButton value="generate" data-testid="batchprocess_mode_generate" sx={{ gap: 0.75, px: 1.5 }}>
              <AutorenewIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontSize: FS_SM, textTransform: 'none' }}>
                Generate
              </Typography>
            </ToggleButton>
            <ToggleButton value="trace" data-testid="batchprocess_mode_trace" sx={{ gap: 0.75, px: 1.5 }}>
              <BiShapeTriangle size={16} />
              <Typography variant="caption" sx={{ fontSize: FS_SM, textTransform: 'none' }}>
                Trace
              </Typography>
            </ToggleButton>
            <ToggleButton value="score" data-testid="batchprocess_mode_score" sx={{ gap: 0.75, px: 1.5 }}>
              <AssessmentOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontSize: FS_SM, textTransform: 'none' }}>
                Score
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Workflow rows - always visible; the preset buttons above
            just rewrite this selection. */}
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
          <DataTable<WorkflowRow>
            rows={WORKFLOW_ROWS}
            cols={workflowCols(workflowSelected, toggleWorkflowStep)}
            rowKey={(r) => r.key}
            testIdPrefix="batchprocess_workflow"
          />
        </Box>

        {/* Source-pick + style only relevant when an answer-pipeline
            substep is selected. */}
        {workflowHasSources && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <CfpSelect<SourcePick>
                label="Add Sources"
                value={pickSelected}
                onChange={setPickSelected}
                options={[
                  { value: 'auto', label: 'Auto' },
                  // { value: 'all', label: 'All' },
                  // { value: 'project', label: 'Project Only' },
                  // { value: 'none', label: 'None' },
                ]}
              />
              {pickSelected === 'auto' && (
                <>
                  <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7 }}>Max Sources:</Typography>
                  <TextField size="small" type="number"
                    value={pickN}
                    onChange={e => setPickN(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
                    inputProps={{ min: 0, max: 10, step: 1 }}
                    sx={{ width: 70, '& .MuiInputBase-input': { fontSize: FS_SM, py: 0.5 } }}
                  />
                </>
              )}
            </Box>
          </>
        )
        }
        {workflowNeedsStyle && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <CfpSelect
                label="Style Template"
                placeholder={styleNames.length === 0 ? 'No style templates' : 'Select a template'}
                value={styleName}
                onChange={setStyleName}
                options={styleNames.map(n => ({ value: n, label: n }))}
                disabled={styleNames.length === 0}
              />
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <FancyButton testId="batchprocess_cancel_btn" disabled={submitting} onClick={onClose}>Cancel</FancyButton>
          <FancyButton
            testId="batchprocess_start_btn"
            solid
            disabled={submitting || todoCount === 0 || !projectsha || !workflowAny}
            onClick={handleStart}
          >
            {startLabel}
            {submitting ? <CircularProgress size={14} sx={{ color: '#fff', ml: 1 }} /> : null}
          </FancyButton>
        </Box>
      </DialogContent>
    </Dialog >
  )
}

// Build the columns for the workflow datatable. Bound to the current
// selection state + toggle callback so the checkboxes round-trip with
// the parent's Set.
function workflowCols(
  selected: Set<WorkflowStep>,
  toggle: (key: WorkflowStep) => void,
): DataTableColumn<WorkflowRow>[] {
  return [
    {
      id: 'select', width: 40, align: 'center', sortable: false, iconColumn: false,
      render: (row) => (
        <Checkbox
          size="small"
          checked={selected.has(row.key)}
          onChange={() => toggle(row.key)}
          data-testid={`batchprocess_workflow_select_${row.key}`}
          sx={{ p: 0.25 }}
        />
      ),
    },
    {
      id: 'label', label: 'Step', width: 140, sortable: false,
      render: (row) => (
        <Typography variant="caption" sx={{ fontSize: FS_SM, fontWeight: 600 }}>
          {row.label}
        </Typography>
      ),
    },
    {
      id: 'description', label: 'Description', width: 0, flex: 1, minWidth: 0, sortable: false,
      render: (row) => (
        <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7 }}>
          {row.description}
        </Typography>
      ),
    },
  ]
}
